import React from 'react';
import clsx from 'clsx';
import { Button } from './button';

type StatefulPaginationProps = {
  currentPage: number;
  totalPages: number;
  maxSlots?: number;
  onPageChange: (page: number) => void;
};

/**
 * A stateful pagination component that uses onClick handlers
 * instead of href-based navigation, styled to match Catalyst Pagination.
 */
const StatefulPagination: React.FC<StatefulPaginationProps> = ({
  currentPage,
  totalPages,
  maxSlots = 7,
  onPageChange
}) => {
  // Generate page numbers to show
  const getPageNumbers = (): (number | 'gap')[] => {
    if (totalPages <= maxSlots) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages: (number | 'gap')[] = [];
    const sideSlots = Math.floor((maxSlots - 3) / 2);

    pages.push(1);

    const start = Math.max(2, currentPage - sideSlots);
    const end = Math.min(totalPages - 1, currentPage + sideSlots);

    if (start > 2) {
      pages.push('gap');
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (end < totalPages - 1) {
      pages.push('gap');
    }

    if (totalPages > 1) {
      pages.push(totalPages);
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <nav aria-label="Page navigation" className="flex gap-x-2">
      {/* Previous */}
      <span className="grow basis-0">
        <Button
          plain
          aria-label="Previous page"
          disabled={currentPage <= 1}
          onClick={() => onPageChange(currentPage - 1)}
        >
          <svg className="stroke-current" data-slot="icon" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
              d="M2.75 8H13.25M2.75 8L5.25 5.5M2.75 8L5.25 10.5"
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Previous
        </Button>
      </span>

      {/* Page numbers */}
      <span className="hidden items-baseline gap-x-2 sm:flex">
        {pageNumbers.map((page, index) =>
          page === 'gap' ? (
            <span
              key={`gap-${index}`}
              aria-hidden="true"
              className="w-9 text-center text-sm/6 font-semibold text-zinc-950 select-none dark:text-white"
            >
              &hellip;
            </span>
          ) : (
            <Button
              key={page}
              plain
              aria-label={`Page ${page}`}
              aria-current={page === currentPage ? 'page' : undefined}
              onClick={() => onPageChange(page)}
              className={clsx(
                'min-w-9 before:absolute before:-inset-px before:rounded-lg',
                page === currentPage && 'before:bg-zinc-950/5 dark:before:bg-white/10'
              )}
            >
              <span className="-mx-0.5">{page}</span>
            </Button>
          )
        )}
      </span>

      {/* Next */}
      <span className="flex grow basis-0 justify-end">
        <Button
          plain
          aria-label="Next page"
          disabled={currentPage >= totalPages}
          onClick={() => onPageChange(currentPage + 1)}
        >
          Next
          <svg className="stroke-current" data-slot="icon" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
              d="M13.25 8L2.75 8M13.25 8L10.75 10.5M13.25 8L10.75 5.5"
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Button>
      </span>
    </nav>
  );
};

export default StatefulPagination;
